import React, { createContext, useContext, useReducer, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

// Action types
const SETTINGS_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  SET_LOADING: 'SET_LOADING'
};

// Transform flat settings to nested structure
const transformSettings = (flatSettings) => {
  return {
    // Company Information
    company: {
      name: flatSettings.companyName || '',
      address: flatSettings.companyAddress || '',
      city: flatSettings.companyCity || '',
      state: flatSettings.companyState || '',
      zipCode: flatSettings.companyZipCode || '',
      country: flatSettings.companyCountry || '',
      phone: flatSettings.companyPhone || '',
      email: flatSettings.companyEmail || '',
      website: flatSettings.companyWebsite || '',
      taxId: flatSettings.companyTaxId || '',
      logoUrl: flatSettings.companyLogo ? (
        flatSettings.companyLogo.startsWith('http') 
          ? flatSettings.companyLogo 
          : `${process.env.REACT_APP_API_URL || 'http://localhost:5002/api'}${flatSettings.companyLogo}`
      ) : null
    },
    
    // Email/SMTP Settings
    email: {
      smtpHost: flatSettings.smtpHost || '',
      smtpPort: flatSettings.smtpPort || 587,
      smtpUser: flatSettings.smtpUsername || '',
      smtpPassword: '', // Never send password back
      smtpSecure: flatSettings.smtpSecure || false,
      fromEmail: flatSettings.smtpFromEmail || '',
      fromName: flatSettings.smtpFromName || ''
    },
    
    // Keep flat properties for backward compatibility
    companyName: flatSettings.companyName || 'Your Company',
    contactPerson: flatSettings.contactPerson || '',
    companyEmail: flatSettings.companyEmail || '',
    companyPhone: flatSettings.companyPhone || '',
    companyWebsite: flatSettings.companyWebsite || '',
    companyAddress: flatSettings.companyAddress || '',
    companyLogo: flatSettings.companyLogo || null,
    
    // SMTP Settings
    smtpHost: flatSettings.smtpHost || '',
    smtpPort: flatSettings.smtpPort || 587,
    smtpUsername: flatSettings.smtpUsername || '',
    smtpFromEmail: flatSettings.smtpFromEmail || '',
    smtpFromName: flatSettings.smtpFromName || '',
    smtpSecure: flatSettings.smtpSecure || false,
    
    // Application Settings
    defaultCurrency: flatSettings.defaultCurrency || 'USD',
    defaultProposalValidityDays: flatSettings.defaultProposalValidityDays || 30,
    enableEmailTracking: flatSettings.enableEmailTracking !== false,
    enableSignatureReminders: flatSettings.enableSignatureReminders !== false,
    reminderIntervalDays: flatSettings.reminderIntervalDays || 7,
    
    // Branding
    primaryColor: flatSettings.primaryColor || '#1976d2',
    secondaryColor: flatSettings.secondaryColor || '#dc004e',
    
    // Security
    sessionTimeoutMinutes: flatSettings.sessionTimeoutMinutes || 480,
    maxLoginAttempts: flatSettings.maxLoginAttempts || 5,
    
    // Proposal Management
    allowDeleteAnyStatus: flatSettings.allowDeleteAnyStatus || false
  };
};

// Initial state
const initialState = {
  settings: transformSettings({}),
  loading: true,
  error: null
};

// Reducer
const settingsReducer = (state, action) => {
  switch (action.type) {
    case SETTINGS_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case SETTINGS_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        settings: transformSettings(action.payload),
        loading: false,
        error: null
      };
    
    case SETTINGS_ACTIONS.FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    case SETTINGS_ACTIONS.UPDATE_SETTINGS:
      return {
        ...state,
        settings: transformSettings(action.payload)
      };
    
    case SETTINGS_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    default:
      return state;
  }
};

// SettingsProvider component
export const SettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(settingsReducer, initialState);
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Fetch settings - public settings when not authenticated, full settings when authenticated
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        dispatch({ type: SETTINGS_ACTIONS.FETCH_START });
        
        let response;
        if (isAuthenticated) {
          // Fetch full settings when authenticated
          response = await api.get('/settings');
          dispatch({
            type: SETTINGS_ACTIONS.FETCH_SUCCESS,
            payload: response.data.settings
          });
        } else {
          // Fetch public settings when not authenticated (for login page logo, etc.)
          response = await api.get('/settings/public');
          dispatch({
            type: SETTINGS_ACTIONS.FETCH_SUCCESS,
            payload: response.data
          });
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        
        dispatch({
          type: SETTINGS_ACTIONS.FETCH_FAILURE,
          payload: error.response?.data?.message || 'Failed to fetch settings'
        });
      }
    };

    if (!authLoading) {
      fetchSettings();
    }
  }, [isAuthenticated, authLoading]);

  // Update company information
  const updateCompanyInfo = async (companyData) => {
    try {
      // Transform nested company data to flat structure for API
      const flatData = {
        companyName: companyData.name,
        companyAddress: companyData.address,
        companyCity: companyData.city,
        companyState: companyData.state,
        companyZipCode: companyData.zipCode,
        companyCountry: companyData.country,
        companyPhone: companyData.phone,
        companyEmail: companyData.email,
        companyWebsite: companyData.website,
        companyTaxId: companyData.taxId
      };
      
      const response = await api.put('/settings/company', flatData);
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update company information';
      return { success: false, error: errorMessage };
    }
  };

  // Update SMTP settings
  const updateSmtpSettings = async (smtpData) => {
    try {
      // Transform nested email data to flat structure for API
      const flatData = {
        smtpHost: smtpData.smtpHost,
        smtpPort: smtpData.smtpPort,
        smtpUsername: smtpData.smtpUser,
        smtpPassword: smtpData.smtpPassword,
        smtpSecure: smtpData.smtpSecure,
        smtpFromEmail: smtpData.fromEmail,
        smtpFromName: smtpData.fromName
      };
      
      const response = await api.put('/settings/smtp', flatData);
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update SMTP settings';
      return { success: false, error: errorMessage };
    }
  };

  // Update application settings
  const updateApplicationSettings = async (appData) => {
    try {
      const response = await api.put('/settings/application', appData);
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update application settings';
      return { success: false, error: errorMessage };
    }
  };

  // Upload company logo
  const uploadLogo = async (logoFile) => {
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const response = await api.post('/settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true, logoPath: response.data.logoPath };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload logo';
      return { success: false, error: errorMessage };
    }
  };

  // Remove company logo
  const removeLogo = async () => {
    try {
      const response = await api.delete('/settings/logo');
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to remove logo';
      return { success: false, error: errorMessage };
    }
  };

  // Test SMTP configuration
  const testSmtpConfig = async (smtpData) => {
    try {
      await api.post('/emails/test-smtp', smtpData);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'SMTP test failed';
      return { success: false, error: errorMessage };
    }
  };

  // Export settings
  const exportSettings = async () => {
    try {
      const response = await api.get('/settings/backup', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `settings-backup-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to export settings';
      return { success: false, error: errorMessage };
    }
  };

  // Import settings
  const importSettings = async (backupData) => {
    try {
      const response = await api.post('/settings/restore', { backupData });
      
      dispatch({
        type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
        payload: response.data.settings
      });
      
      return { success: true, restoredFields: response.data.restoredFields };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to import settings';
      return { success: false, error: errorMessage };
    }
  };

  // Get logo URL
  const getLogoUrl = () => {
    if (!state.settings.companyLogo) return null;
    
    // If it's already a full URL, return as is
    if (state.settings.companyLogo.startsWith('http')) {
      return state.settings.companyLogo;
    }
    
    // Ensure the path starts with /uploads for static file serving
    if (state.settings.companyLogo.startsWith('/uploads/')) {
      return state.settings.companyLogo;
    }
    // If path doesn't start with /uploads/, prepend it
    return `/uploads/${state.settings.companyLogo.replace(/^\//, '')}`;
  };

  // Refresh settings manually
  const refreshSettings = async () => {
    try {
      dispatch({ type: SETTINGS_ACTIONS.FETCH_START });
      
      const response = await api.get('/settings');
      
      dispatch({
        type: SETTINGS_ACTIONS.FETCH_SUCCESS,
        payload: response.data.settings
      });
      
      return { success: true };
    } catch (error) {
      console.error('Failed to refresh settings:', error);
      
      dispatch({
        type: SETTINGS_ACTIONS.FETCH_FAILURE,
        payload: error.response?.data?.message || 'Failed to refresh settings'
      });
      
      return { success: false, error: error.response?.data?.message || 'Failed to refresh settings' };
    }
  };

  // Get currency symbol
  const getCurrencySymbol = (currency = null) => {
    const curr = currency || state.settings.defaultCurrency;
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CAD: 'C$',
      AUD: 'A$',
      CHF: 'CHF',
      CNY: '¥',
      SEK: 'kr',
      NZD: 'NZ$'
    };
    return symbols[curr] || curr;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return getCurrencySymbol() + '0.00';
    const currency = state.settings.defaultCurrency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const value = {
    settings: state.settings,
    loading: state.loading,
    error: state.error,
    updateCompanyInfo,
    updateSmtpSettings,
    updateApplicationSettings,
    uploadLogo,
    removeLogo,
    testSmtpConfig,
    exportSettings,
    importSettings,
    refreshSettings,
    getLogoUrl,
    getCurrencySymbol,
    formatCurrency
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;